import CollectionDataGrid from '@/components/CollectionDataGrid'
import { pb, usePaginatedCollection, useRecord } from '@/pb'
import { useInputState } from '@/utils'
import BarChartIcon from '@mui/icons-material/BarChart'
import DeleteIcon from '@mui/icons-material/Delete'
import QrCodeIcon from '@mui/icons-material/QrCode'
import {
  Box,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress,
  TextField,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useState } from 'react'
import QRCode from "react-qr-code"

import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import MetricChart from './UserApp/MetricChart'
import { createMusicWorkout, enrichWorkout, finishWorkout, pauseWorkout, resumeWorkout, startWorkout } from './UserApp/util'

import { PBAutocomplete } from '@/components/PBAutocomplete'

const Editor = ({ row, onClose }) => {
  const theme = useTheme()
  const [name, setName] = useInputState(row?.title || '')
  const [saving, setSaving] = useState(false)

  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [participants, setParticipants] = useState([])

  const handleAddUser = () => {
    if (!selectedUser) return

    if (participants.some(p => p.userObj.id === selectedUser.id)) {
      setSelectedUser(null)
      return
    }

    setParticipants(prev => [
      ...prev,
      { userObj: selectedUser, deviceObj: null }
    ])
    setSelectedUser(null)
  }

  const handleRemoveParticipant = (userId) => {
    setParticipants(prev => prev.filter(p => p.userObj.id !== userId))
  }

  const handleDeviceChange = (userId, newDeviceObj) => {
    setParticipants(prev => prev.map(p =>
      p.userObj.id === userId ? { ...p, deviceObj: newDeviceObj } : p
    ))
  }

  const handleSave = async () => {
    if (!selectedTemplate) return

    setSaving(true)
    try {
      const groupData = {
        name,
        state: "paused",
        templateId: selectedTemplate.id,
      }

      const savedGroup = await pb.collection("workout_groups").create(groupData)
      const groupId = savedGroup.id

      const workoutPromises = participants
        .filter((userInGroup) => userInGroup.deviceObj)
        .map((userInGroup) =>
          createMusicWorkout({
            templateId: selectedTemplate.id,
            deviceId: userInGroup.deviceObj.id,
            userId: userInGroup.userObj.id,
            groupId,
          })
        )

      await Promise.all(workoutPromises)
      onClose()
    } catch (error) {
      console.error("Save failed:", error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <DialogTitle>Групповая тренировка</DialogTitle>
      {saving && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}

      <DialogContent className="editorForm" sx={{ minWidth: { sm: '500px' } }}>
        <TextField
          label="Название"
          value={name}
          onChange={setName}
          fullWidth
          margin="dense"
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Типовая тренировка
          </Typography>
          <PBAutocomplete
            collectionName="music_workout_templates"
            label="Выберите типовую тренировку..."
            value={selectedTemplate}
            onChange={(_, newValue) => setSelectedTemplate(newValue)}
            searchFields={['name', 'title']}
            getOptionLabel={(option) => option.name || option.title || option.id}
            sx={{ flexGrow: 1 }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            Спортсмены
          </Typography>

          <Box sx={{ maxHeight: '500px', overflowY: 'auto', mb: 3, pr: 1 }}>
            {participants.length === 0 ? (
              <Typography variant="body2" color="text.disabled" align="center" sx={{ py: 2 }}>
                Добавьте спортсменов
              </Typography>
            ) : (
              participants.map((p) => (
                <Box
                  key={p.userObj.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 2,
                    p: 1.5,
                    bgcolor: '#303030',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.primary.main}`
                  }}
                >
                  <Typography sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                    {p.userObj.name || p.userObj.email || `[${p.userObj.id}]`}
                  </Typography>

                  <PBAutocomplete
                    collectionName="devices"
                    label="Устройство"
                    value={p.deviceObj}
                    onChange={(_, newValue) => handleDeviceChange(p.userObj.id, newValue)}
                    searchFields={['deviceId', 'ip']}
                    getOptionLabel={(option) => option.deviceId || option.ip || option.id}
                    getOptionDisabled={(option) =>
                      participants.some(
                        participant =>
                          participant.userObj.id !== p.userObj.id &&
                          participant.deviceObj?.id === option.id
                      )
                    }
                    sx={{ width: '200px', flexShrink: 0 }}
                  />

                  <IconButton
                    onClick={() => handleRemoveParticipant(p.userObj.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <PBAutocomplete
              collectionName="users"
              label="Найти пользователя..."
              value={selectedUser}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              searchFields={['name', 'email']}
              getOptionLabel={(option) => option.name || option.email || `[${option.id}]`}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              onClick={handleAddUser}
              disabled={!selectedUser}
              sx={{ height: '40px' }}
            >
              Добавить
            </Button>
          </Box>
          
        </Box>

      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !selectedTemplate}
        >
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </>
  )
}


const QrButton = ({ id, name }) => {
  const [open, setOpen] = useState(false)

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      <IconButton
        onClick={handleClickOpen}
        color='primary'
      >
        <QrCodeIcon />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        role="alertdialog"
      >
        <DialogTitle id="alert-dialog-title">
          {name}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
          <div style={{ height: "auto", padding: "1rem", width: "100%", background: "#fff" }}>
            <QRCode
              size={256}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              value={id}
              viewBox={`0 0 256 256`}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

    </>
  )
}


const StatsButton = ({ row }) => {
  const [open, setOpen] = useState(false)

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const {
    records: workoutTemplates,
    loaded: workoutTemplatesLoaded
  } = usePaginatedCollection('music_workout_templates', {
    page: 1,
    perPage: 30,
    sort: '-created',
    expand: 'musicId'
  })

  const {
    records: music_workouts,
    totalItems,
    loaded,
  } = usePaginatedCollection('music_workouts', {
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
    sort: '-created',
    filter: `groupId = "${row.id}"`
  })

  return (
    <>
      <IconButton
        onClick={handleClickOpen}
        color='primary'
      >
        <BarChartIcon />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        role="alertdialog"
      >
        <DialogTitle id="alert-dialog-title">
          Монитогоринг групповой тренировки
        </DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
          {
            music_workouts.map((workout) => {
              const enrichedWorkout = enrichWorkout(workout, workoutTemplates)

              return (
                <Box sx={{ mt: 1.5 }} key={workout.id}>
                  <MetricChart timestamps={enrichedWorkout?.punchTimestamps} vals={enrichedWorkout?.punchForces} title='Сила удара' />
                </Box>
              )
            })
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

    </>
  )
}

const PlayPauseButton = ({ row }) => {
  const workoutGroup = useRecord("workout_groups", row.id)

  const state = workoutGroup?.state || "paused"
  const isPlaying = state === "playing"
  const isEnded = state === "ended"

  const handleTogglePlayPause = async () => {
    const currentTime = Date.now()

    try {
      if (isPlaying) {
        await pauseWorkout({
          groupId: row.id,
          lastPauseTime: currentTime,
        })
      } else if (workoutGroup?.startTime > 0) {
        await resumeWorkout({
          groupId: row.id,
          resumeTime: currentTime,
        })
      } else {
        await startWorkout({
          groupId: row.id,
          startTime: currentTime,
        })
      }
    } catch (error) {
      console.error("Error toggling workout group state:", error)
    }
  }

  const handleFinish = async () => {
    try {
      await finishWorkout({
        groupId: row.id,
      })
    } catch (error) {
      console.error("Error finishing workout:", error)
    }
  }

  if (!workoutGroup) return null

  if (isEnded) {
    return <span>Завершено</span>
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <IconButton onClick={handleTogglePlayPause} color="primary">
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>

      {workoutGroup.startTime > 0 && (
        <Button size="small" variant="outlined" color="error" onClick={handleFinish}>
          Завершить
        </Button>
      )}
    </Box>
  )
}


const columns = [
  { field: 'name', headerName: 'Название', width: 250 },
  {
    field: 'expand.templateId',
    headerName: 'Типовая тренировка',
    width: 250,
    renderCell: ({ row }) => {
      const workout = row?.expand?.templateId
      return <span>{workout?.name || '—'}</span>
    },
  },
  {
    field: 'id',
    headerName: 'QR приглашение',
    width: 150,
    renderCell: ({ row }) => <QrButton id={row.id} name={row.name} />
  },
  {
    field: 'state',
    headerName: 'Управление',
    width: 150,
    renderCell: ({ row }) => <PlayPauseButton row={row} />
  },
  {
    field: 'stats',
    headerName: 'Статистика',
    width: 250,
    renderCell: ({ row }) => <StatsButton row={row} />
  },
]

export default function WorkoutGroups({ title }) {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const {
    records: workout_groups,
    totalItems,
    loaded,
  } = usePaginatedCollection('workout_groups', {
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
    sort: '-created',
    expand: 'templateId',
  })

  return <>
    <CollectionDataGrid
      collectionName={title}
      collection={workout_groups}
      columns={columns}
      loading={!loaded}
      editor={Editor}
      serverPagination
      rowCount={totalItems}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
    />
  </>

}